import { createSlice } from '@reduxjs/toolkit';
import dayjs from 'dayjs';

// Cameras — the camera page's state.
//
// 🛑 THE CAMERA LIST IS NOT HERE. A camera is a device row, so the list is derived from
// `state.Devices` in selectors.js and this slice holds only what is genuinely the camera
// page's own: events, streams, thumbnails, clips, sync sources and the window/selection.
// Everything stores what the server said; every count and grouping is derived in
// selectors.js.

/**
 * The grid the range snaps to, in minutes. It is the picker's `minuteStep` and the finest
 * bucket the timeline will draw — a chart with a bar per minute is noise, not detail.
 */
export const BUCKET_MINUTES = 15;

/**
 * Shortest range the surface will hold.
 *
 * 🛑 ONE MINUTE, and it used to be 15 — which silently made a short clip impossible. A clip
 * may be 20 minutes at most and is usually far shorter ("the two minutes either side of that
 * door opening"), so a floor of 15 quietly widened every such request to a quarter of an hour
 * and handed back a window nobody drew.
 *
 * ⚠️ NOT the same number as BUCKET_MINUTES any more, and they were only ever equal by
 * coincidence: that one is the chart's grid and the picker's step, this one is a floor on the
 * range itself. Re-joining them re-breaks short clips.
 */
export const MIN_RANGE_MINUTES = 1;

/**
 * The widest range one view may cover.
 *
 * 🛑 Nothing is stored: every event in the range is read live from the vendor when the
 * range changes, so this is a bound on THEIR work as much as ours — and the server refuses
 * more (CAMERA_EVENTS_MAX_WINDOW_SECONDS in constants/cameraEvents.js on the App Server
 * holds the same number; a picker offering more than the server accepts is a validation
 * error the operator cannot act on).
 *
 * 🛑 ONE day, down from seven, and the vendor's SAMPLE RATE is what sets it. Detections
 * come from Verkada's `object_counts`, which has no `interval` parameter: it returns raw
 * ~20 s samples, 200 to a page. A day is ~4,300 samples over ~22 pages. A week would be
 * ~30,000 over ~152 — not a wide read, an outage. "Was anyone here over the long weekend"
 * is a question this surface can no longer answer, and answering it needs
 * `/analytics/occupancy_trends` (which returns series, but must be switched on per camera
 * in Command).
 */
export const MAX_RANGE_DAYS = 1;

/**
 * Longest window one clip may cover.
 *
 * 🛑 NOT MAX_RANGE_DAYS, and the gap is the point: the picker happily produces a window 72x
 * larger than any clip may be, so the export control gates on this and says why.
 * ⚠️ THE FALLBACK ONLY. Every camera carries its own `maxClipSeconds` (Verkada 1200, VORTEX 30) and
 * callers pass it as `maxSeconds`; this is what applies when a camera has not said.
 */
export const CLIP_MAX_MINUTES = 20;

/**
 * SHORTEST a clip may be — and it is a number of its own, not the window's floor.
 *
 * 🛑 IT USED TO BE `min(MIN_RANGE_MINUTES, the camera's cap)`, which on VORTEX made the floor and
 * the ceiling THE SAME NUMBER: a clip there may be 30 s, so the bracket had to be exactly 30 s,
 * and every attempt to trim it snapped back to full length the instant the hand let go. The
 * handles were controls that could not change anything. On Verkada it was milder and still wrong
 * — nothing under a minute could be marked, so "just the moment the door opened" was a minute of
 * footage either way.
 *
 * ⚠️ A WINDOW's floor is a different question with a different reason. That one is about a PERIOD
 * TO FETCH, bucketed at BUCKET_MINUTES; this is about a file a person will watch, and ten seconds
 * of it is a perfectly ordinary thing to want.
 */
export const CLIP_MIN_SECONDS = 10;

/**
 * How far BACK the window may be moved. A different question from how WIDE it may be, and
 * conflating the two is the whole bug this constant exists to end.
 *
 * 🛑 THE PICKER USED TO REFUSE EVERY DATE OLDER THAN `MAX_RANGE_DAYS`. One day is the cap
 * on the WIDTH of a single read — a bound on how many ~20 s samples the vendor is asked to
 * page through in one go — and it was also being used as the oldest date on the calendar,
 * so "show me the loading bay a month ago" was unreachable: the day was greyed out, and a
 * start that arrived any other way was clamped straight back to yesterday. Retention is
 * measured in months and the vendor answers an old window perfectly happily; the only
 * thing that may not grow is the WIDTH of one read.
 *
 * This is the GUARD, deliberately looser than the guide. What the calendar actually offers
 * is the camera's own retention (see `selectLookbackDays`), because retention is a
 * per-camera promise and a fixed number here would either grey out footage a 365-day
 * camera really has or offer a year of empty days for a 30-day one. A year is simply the
 * point past which a date is a typo rather than a question.
 */
export const MAX_LOOKBACK_DAYS = 365;

/**
 * The calendar reaches at least this far back however little retention the camera reports
 * — including the cameras that report none at all, where `retentionDays` is null because
 * no sync has ever written it. Refusing to look back on a camera whose retention we simply
 * do not know is an answer about our sync, not about their footage.
 */
export const MIN_LOOKBACK_DAYS = 30;

/**
 * The cache key for one events window.
 *
 * The window IS the pagination (there is no page parameter on the server), so the
 * window is also the identity of a result — the same camera over a different hour is
 * a different answer, not a newer one.
 */
export const eventsKey = (deviceID, startDate, endDate) => `${deviceID}|${startDate}|${endDate}`;

/**
 * 🛑 The one place `downloadUrl` is dropped. It is minted per read and lives 5 minutes, so a
 * copy in the store is a link that expires without saying so — every clip write goes through here.
 */
const stripUrl = ({ downloadUrl, downloadExpiresAt, ...clip }) => clip;

const initialState = {
	// 🛑 An ORG-WIDE answer, and the only camera fact this slice still stores. A site with
	// no cameras is not an org that never synced, and reading the scoped list told operators
	// in a camera-less building to reconnect a working integration. Nothing else can answer
	// it: the scoped devices bucket is the site's, and the 'all' bucket is not loaded in a
	// site view.
	orgHasCameras: false,

	// eventsKey() → { events, supported, truncated }. `supported` carries the difference
	// between "nothing happened" and "this vendor has no event surface at all".
	//
	// 🛑 Camera events ONLY. Door unlocks used to be merged in server-side, scoped to the
	// camera's floor plan; removed, because no camera↔door association exists — the rows
	// claimed a camera watched a door nobody had said it watched.
	events: {},

	// deviceID → the credential the server signed, expiry in epoch SECONDS. One serves
	// every window, so this is a cache with an expiry — see selectStreamIsStale.
	streams: {},

	// deviceID → { image, capturedAt } — one still frame per camera, or { error }.
	//
	// 🛑 This is what the WALL draws. Forty tiles each holding an HLS session is forty
	// vendor connections for a screen nobody is watching forty of; a still is one GET, and
	// the picture only starts moving under the pointer. `capturedAt` is what stops a frame
	// from an hour ago passing as the current one — see THUMBNAIL_MAX_AGE_SECONDS.
	thumbnails: {},

	// ⚠️ /verkada and /vortex are adminOnly, so a site-scoped caller 403s and this stays
	// null. Nothing may depend on it — connection state comes from the module flags.
	sources: { verkada: null, vortex: null },

	// The Integrations settings page's "Verify" button: { ok, cameraCount } once it answers.
	verify: { loading: false, result: null },

	// deviceID → { ids: [clipID], loading } and byID → the row the server returned.
	//
	// 🛑 NO `downloadUrl` HERE, EVER. It expires in 5 minutes and is re-minted on every read,
	// so a stored one is a link that works until it silently does not. The download re-reads
	// the clip and uses the URL from that answer — see downloadClip in the saga.
	//
	// `watching` = the clips THIS session asked for and is still following, newest first. It is what
	// makes a clip a background job (the bell's jobs section + the top-bar chip — see
	// `library/hooks/useBackgroundJobs`), and what the poll saga loops over. Deliberately not "every
	// clip that says processing": `byID` also holds rows loaded from the server, and a job wedged
	// vendor-side would then spin in the top bar of an operator who never asked for it.
	clips: { byID: {}, forDevice: {}, requesting: {}, watching: [] },

	loading: {
		// `list` lives on the bucket: two sites can be in flight and one flag cannot say so.
		sources: false,
		stream: {}, // deviceID → bool
		thumbnail: {}, // deviceID → bool
		events: {} // eventsKey → bool
	},

	error: null,

	// Selection and window live here because the modal, timeline and table describe the
	// SAME window; two local copies is how they end up showing different periods.
	ui: {
		selectedDeviceID: null,

		/*
		 * Is the operator BRACKETING a clip, or navigating?
		 *
		 * 🛑 The two are different gestures on the same chart, and one drag cannot mean both.
		 * Off: the chart is a map — drag travels through time, which is what an operator does
		 * ninety per cent of the time and had no gesture at all. On: drag brackets the range
		 * the clip will cover, and the yellow handles say so.
		 *
		 * It is state rather than a local flag because the button that turns it on lives under
		 * the chart, in a different component from the chart itself.
		 */
		clipMode: false,

		/*
		 * THE PERIOD THE CLIP WILL COVER — `{ from, to }` as ISO instants, or null when nothing
		 * is bracketed.
		 *
		 * 🛑 IT IS NOT `from`/`to`. THAT WAS THE BUG, and it was a deep one: the amber band used
		 * to BE the loaded window, on the reasoning that "the clip you mark is the window you can
		 * play". One fact, drawn once. Except the two are not one fact — the window is what has
		 * been FETCHED and the bracket is what the operator has CHOSEN — and tying them together
		 * meant every nudge of the bracket re-filtered the period, re-minted the stream and
		 * reloaded the picture. So the one thing an operator does constantly while trimming a
		 * clip, moving it a little, was the most expensive gesture on the page, and it dragged
		 * playback back to the top of the new period each time. Moving it now costs one reducer.
		 *
		 * 🛑 AND IT IS NOT BOUNDED BY THE WINDOW EITHER. The server cuts from the RECORDING, not
		 * from what this browser happens to have downloaded, so a bracket may sit anywhere in
		 * retention. What it may not be is longer than a clip — see `clipRangeFrom`, which is the
		 * one place that rule lives now that `setRange` has been given its window back.
		 *
		 * ⚠️ Cleared with the mode, and by the cut: a bracket outliving the errand it was opened
		 * for is a period the next drag would silently re-use.
		 */
		clipRange: null,

		// 'events' | 'clips' — which side of the activity panel beside the player is open.
		//
		// 🛑 HERE, not in the panel's own useState, because exporting a clip has to be able
		// to MOVE it: the button that cuts the file lives on the other side of the layout,
		// and a local flag would leave the operator watching a tab that says nothing
		// happened while their clip is being made in the one they cannot see.
		activityTab: 'events',

		// 🛑 'all' (the wall) | 'one' (the detail view), and it is INDEPENDENT of the
		// selection — which is always set. Switching back to the wall keeps the camera
		// selected, so flipping to the detail view again lands on the one the operator
		// was looking at. It does NOT mark that camera's tile: nothing here ever clears
		// the selection, so a tile styled on it wore a ring from the first click until
		// the tab closed — see BrowseWallCameraTile.
		//
		// The detail view has no back button because this switch is the way out, and a
		// second control doing the same thing leaves two answers to "where am I".
		view: 'all',

		// Wall tile order, spaceKey → [deviceID]. Session-only and deliberately so: it
		// is a "let me put these three side by side while I look at them" affordance,
		// not a property of the camera. Nothing is written to the server, no floor plan
		// moves, and a camera never leaves the space it is grouped under — see
		// selectCameraWall for how a partial order is applied.
		order: {},

		// Which group of the wall is on screen: a tree site key ("360", "orphan"), a tree
		// space key ("360:812"), or null for everything in the scope.
		//
		// 🛑 A FILTER, never a scope. `App.selectedSiteID` — the topbar's dropdown — decides
		// which bucket the SERVER fills, and this narrows what the wall draws out of it.
		// The rail sets this and nothing else: a left-hand list that quietly moved the
		// global site would be a second answer to "which site am I in" on a screen that
		// already has one, and the two disagree the moment somebody uses the other control.
		groupKey: null,

		// 🛑 THERE IS NO `tab`. Live and History were a stored mode with two buttons setting
		// it, and the mode was already implied by the two fields below it: a window whose end
		// is the live edge IS live, and one that has been dragged into the past IS playback.
		// Storing it alongside them let the two disagree — the tab said `live` while the
		// window sat on Tuesday morning, and whichever surface read the wrong one was wrong.
		// See `selectIsLive`, which is the only place the question is now answered.
		filter: 'all', // 'all' | 'online' | 'offline' | 'unplaced'
		search: '',
		// 🛑 ISO instants, not a day plus two offsets into it. The window used to be
		// `day + startIdx/endIdx` over a 96-bucket grid, which made "yesterday evening
		// until tonight" — the ordinary way anyone describes what they want to check —
		// unrepresentable. A range is two moments; nothing about it is a day.
		//
		// `null` means "the last hour, ending now", resolved in selectWindow so the default
		// is never a stale instant frozen at import time.
		from: null,
		to: null,
		planOpen: false,
		// Epoch seconds. The player subscribes; the table does not reach into it.
		seekTo: null,

		/*
		 * The ONE thing drawn as a band on the chart: `{ from, to }` in epoch seconds, or null.
		 *
		 * 🛑 IT IS THE EVENT SOMEONE CLICKED, not the window. The chart used to paint the whole
		 * loaded period and hang two edge markers off it, which said "this is your selection"
		 * about a range nobody selected — it is just what happens to be loaded, it covers most
		 * of the chart, and so the colour meant nothing. What an operator actually points at is
		 * one event; that is what earns a band.
		 *
		 * ⚠️ Transient by design. A click anywhere on the chart clears it: the band answers
		 * "the row you just pressed is HERE", and once the hand has moved on the answer is stale.
		 */
		highlight: null,
		// 🛑 Bumped by every seek, INCLUDING one to the instant already asked for. The
		// offset alone is not enough: click a row, let the clip play on, click the same row
		// again and the value has not changed, so nothing downstream re-runs and the click
		// does nothing. The counter is what makes the second click an event.
		seekNonce: 0,

		// The chart's own magnification: null = whole days, or an explicit { startDate, endDate }
		// span to draw instead. 🛑 It narrows the PICTURE only — the events request stays keyed on
		// whole days, so zooming re-buckets what is already in the browser and costs no request.
		timelineZoom: null
	}
};

/**
 * Keeps a range askable: no older than MAX_LOOKBACK_DAYS, no wider than MAX_RANGE_DAYS, at
 * least MIN_RANGE_MINUTES wide, and never reaching past NOW.
 *
 * 🛑 `anchor` NAMES THE END THE OPERATOR JUST MOVED, and it is the difference between a
 * control that works and one that appears broken. Every rule below can only be satisfied
 * by moving one of the two instants, and until this existed the answer was always "move
 * the start" — so dragging the START back to last month widened the range past the cap and
 * the fix was to drag the start forward again, landing you back where you began. The end
 * you touched stays put; the other one gives way.
 *
 * ⚠️ Reading the clock inside a reducer is deliberate and it is the only place it happens.
 * The alternative — clamping in the picker — puts the rule wherever a control is added
 * next, and this one is not cosmetic: past `now` we ask the vendor for footage that does
 * not exist yet, which comes back looking like a broken camera rather than like "not yet".
 *
 * @param {{from: string, to: string, anchor?: 'start'|'end'}} range - ISO instants, and
 *   which end must survive. Defaults to `end`: a range arriving with no opinion is the
 *   recent side being investigated.
 * @returns {{from: string, to: string}} ISO instants, clamped
 */
const clampRange = ({ from, to, anchor = 'end' }) => {
	const now = dayjs();
	const keepStart = anchor === 'start';
	const floor = now.subtract(MAX_LOOKBACK_DAYS, 'day');

	let start = dayjs(from);
	let end = dayjs(to);
	if (!start.isValid()) start = end.isValid() ? end.subtract(MIN_RANGE_MINUTES, 'minute') : now;
	if (!end.isValid()) end = start.add(MIN_RANGE_MINUTES, 'minute');

	// Neither end may sit in the future or before the oldest date we will ask about.
	if (end.isAfter(now)) end = now;
	if (start.isAfter(now)) start = now;
	if (start.isBefore(floor)) start = floor;
	if (end.isBefore(floor)) end = floor;

	// The right way round, and at least one bucket wide.
	if (end.diff(start, 'minute') < MIN_RANGE_MINUTES) {
		if (keepStart) end = start.add(MIN_RANGE_MINUTES, 'minute');
		else start = end.subtract(MIN_RANGE_MINUTES, 'minute');
	}

	// The width cap — one read, not one period. A month-old start keeps its place and the
	// END comes back to meet it; that is what makes looking a month back possible at all.
	if (end.diff(start, 'day', true) > MAX_RANGE_DAYS) {
		if (keepStart) end = start.add(MAX_RANGE_DAYS, 'day');
		else start = end.subtract(MAX_RANGE_DAYS, 'day');
	}

	// A start-anchored push can land the end past the live edge (a start inside the last
	// quarter-hour). Then the end is immovable and the start is the one that gives way,
	// whatever the anchor asked for — a window that reaches into the future is not a
	// window, and MIN_RANGE_MINUTES is far below the cap so this cannot break it.
	if (end.isAfter(now)) end = now;
	if (end.diff(start, 'minute') < MIN_RANGE_MINUTES) start = end.subtract(MIN_RANGE_MINUTES, 'minute');

	return { from: start.toISOString(), to: end.toISOString() };
};

/**
 * A BRACKET, KEPT ASKABLE — ordered, at least a minute, never longer than a clip may be, and
 * never reaching past the live edge. Null in, null out: that is how the bracket is cleared.
 *
 * 🛑 IT IS THE ONE PLACE THE CLIP CAP LIVES. It used to be enforced on `setRange` — on the
 * WINDOW — because the window was the bracket, which meant choosing a period to look at and
 * choosing a period to cut were the same act: opening the bracket shrank an hour of loaded
 * footage to twenty minutes, and closing it left the operator with a period they never picked.
 * The window is a window again; this caps the only thing the cap was ever about.
 *
 * ⚠️ `anchor: 'start'` keeps the FROM — the instant a rightwards sweep began — and anything else
 * keeps the TO, the same reading `clampRange` gives it.
 *
 * ⚠️ The live edge wins over the anchor, exactly as it does for a window: there is no footage in
 * the future to cut, so an end past now comes back and the start gives way if it has to.
 */
export const clipRangeFrom = (payload) => {
	if (!payload) return null;
	const { from, to, anchor, maxSeconds } = payload;
	const now = dayjs();

	/*
	 * 🛑 THE CEILING IS PER CAMERA — a VORTEX clip may be 30 s, a Verkada one twenty minutes — and
	 * the FLOOR is its own small number rather than the window's (see CLIP_MIN_SECONDS). Deriving
	 * the floor from the ceiling is what made a VORTEX bracket rigid: both came to 30 s, so the
	 * only length it could take was the one it opened at.
	 *
	 * ⚠️ Still `min(...)` with the ceiling, because a floor above the ceiling is a bracket no
	 * operator could ever place — a camera whose cap ever falls below ten seconds gets a bracket
	 * pinned at that cap, which is at least the truth about what it can cut.
	 */
	const maxMinutes = maxSeconds > 0 ? maxSeconds / 60 : CLIP_MAX_MINUTES;
	const minMinutes = Math.min(CLIP_MIN_SECONDS / 60, maxMinutes);
	let start = dayjs(from);
	let end = dayjs(to);
	if (!start.isValid() && !end.isValid()) return null;
	if (!start.isValid()) start = end.subtract(minMinutes, 'minute');
	if (!end.isValid()) end = start.add(minMinutes, 'minute');
	// A sweep leftwards hands its two instants over in the order the hand met them.
	if (end.isBefore(start)) [start, end] = [end, start];

	const keepStart = anchor === 'start';
	const width = () => end.diff(start, 'minute', true);

	if (width() < minMinutes) {
		if (keepStart) end = start.add(minMinutes, 'minute');
		else start = end.subtract(minMinutes, 'minute');
	}
	if (width() > maxMinutes) {
		if (keepStart) end = start.add(maxMinutes, 'minute');
		else start = end.subtract(maxMinutes, 'minute');
	}

	if (end.isAfter(now)) {
		end = now;
		if (end.diff(start, 'minute', true) < minMinutes) start = end.subtract(minMinutes, 'minute');
	}

	return { from: start.toISOString(), to: end.toISOString() };
};

const camerasSlice = createSlice({
	name: 'cameras',
	initialState,
	reducers: {
		// ── Does this org own any cameras at all ─────────────────────────────────

		/*
		 * 🛑 THE ONLY CAMERA READ LEFT, and it is one question, not a list. The empty state
		 * asks "has this org any cameras" — and in a site view nothing in the store can
		 * answer it, because the devices bucket is that site's. So this asks the portfolio,
		 * once, and keeps the boolean.
		 *
		 * It is its own action rather than a devices read for the reason it always was:
		 * `devicesActions.loadDevices` is takeLatest, so dispatching it with `siteID: 'all'`
		 * would cancel the scoped read the shell is waiting on and leave the site view
		 * permanently empty. Different questions must be latest of different things.
		 */
		probeOrgCameras: () => {},

		probeOrgCamerasResult: (state, action) => {
			state.orgHasCameras = action.payload.orgHasCameras;
		},

		// ── GET /cameras/:deviceID/events ────────────────────────────────────────
		loadEvents: (state, action) => {
			const { deviceID, startDate, endDate } = action.payload;
			state.loading.events[eventsKey(deviceID, startDate, endDate)] = true;
		},
		loadEventsSuccess: (state, action) => {
			const { deviceID, startDate, endDate, ...result } = action.payload;
			const key = eventsKey(deviceID, startDate, endDate);
			state.loading.events[key] = false;
			state.events[key] = result;
		},
		loadEventsError: (state, action) => {
			const { deviceID, startDate, endDate, error } = action.payload;
			state.loading.events[eventsKey(deviceID, startDate, endDate)] = false;
			state.error = error;
		},

		// ── Live view ────────────────────────────────────────────────────────────
		loadStream: (state, action) => {
			state.loading.stream[action.payload.deviceID] = true;
		},
		loadStreamSuccess: (state, action) => {
			const { deviceID, requestedAt, ...descriptor } = action.payload;
			state.loading.stream[deviceID] = false;
			state.streams[deviceID] = { ...descriptor, requestedAt };
		},
		loadStreamError: (state, action) => {
			const { deviceID, error } = action.payload;
			state.loading.stream[deviceID] = false;
			// Per-camera, and stored as an ANSWER: the player stops asking rather than
			// looping on every render.
			state.streams[deviceID] = { error };
		},
		clearStream: (state, action) => {
			delete state.streams[action.payload.deviceID];
		},

		// ── The wall's still frames ───────────────────────────────────────────────
		loadThumbnail: (state, action) => {
			state.loading.thumbnail[action.payload.deviceID] = true;
		},
		loadThumbnailSuccess: (state, action) => {
			const { deviceID, image, capturedAt } = action.payload;
			state.loading.thumbnail[deviceID] = false;
			state.thumbnails[deviceID] = { image, capturedAt };
		},
		loadThumbnailError: (state, action) => {
			const { deviceID, error, transient } = action.payload;
			state.loading.thumbnail[deviceID] = false;
			// Rate-limited (429) says nothing about the camera, so the next sweep asks again.
			if (transient) return;
			// 🛑 Stored as an ANSWER so the grid stops asking; the previous frame is kept.
			state.thumbnails[deviceID] = { ...state.thumbnails[deviceID], error };
		},

		// ── Provider sync (admin only) ───────────────────────────────────────────
		loadSources: (state) => {
			state.loading.sources = true;
		},
		loadSourcesSuccess: (state, action) => {
			state.loading.sources = false;
			state.sources = { ...state.sources, ...action.payload };
		},
		loadSourcesError: (state) => {
			// Silent: a non-admin is EXPECTED to 403 here, and a toast per poll would tell
			// them off for a page they may legitimately see.
			state.loading.sources = false;
		},
		syncSource: (state, action) => {
			const { provider } = action.payload;
			state.sources[provider] = { ...state.sources[provider], syncing: true };
		},
		/* ── Clips ──────────────────────────────────────────────────────────────────
		 * A clip is a ROW, not a delivery: the POST answers with an id and the client polls.
		 * `strip` is what keeps the expiring URL out of the store.
		 */
		requestClip: (state, action) => {
			state.clips.requesting[action.payload.deviceID] = true;
			// The bracket has done its job. Left on, the chart would still be in selection
			// mode over a clip already being cut, and the next drag would silently re-bracket.
			state.ui.clipMode = false;
			state.ui.clipRange = null;
			// 🛑 AND THE MAGNIFICATION GOES WITH IT, exactly as it does on Cancel. The bar is
			// closed in around the bracket while there IS one (see bracketView) — on VORTEX that
			// is a view a minute wide — and leaving it there after the cut left the operator
			// looking at the whole page through a keyhole, for a band that is no longer drawn.
			// The two ways out of clip mode must leave the same surface behind.
			state.ui.timelineZoom = null;
			// 🛑 Show the list the clip is about to land in. The button is under the player;
			// the row it creates appears in a tab that may not be open, so without this the
			// only feedback for "Export clip" was a spinner on the button that then stopped —
			// with the result on a surface the operator had to know to go and find.
			state.ui.activityTab = 'clips';
		},
		requestClipSuccess: (state, action) => {
			const clip = stripUrl(action.payload.clip);
			state.clips.requesting[clip.deviceID] = false;
			state.clips.byID[clip.clipID] = clip;
			// Follow it from here on, wherever the operator goes. Newest first, and idempotent for the
			// same reason the ids list below is: the server keys on (deviceID, start, end).
			if (!state.clips.watching.includes(clip.clipID)) state.clips.watching.unshift(clip.clipID);
			const ids = state.clips.forDevice[clip.deviceID]?.ids || [];
			// Idempotent server-side on (deviceID, start, end), so the same id can come back.
			state.clips.forDevice[clip.deviceID] = {
				...state.clips.forDevice[clip.deviceID],
				ids: ids.includes(clip.clipID) ? ids : [clip.clipID, ...ids]
			};
		},
		requestClipError: (state, action) => {
			state.clips.requesting[action.payload.deviceID] = false;
		},
		loadClips: (state, action) => {
			const deviceID = action.payload.deviceID;
			state.clips.forDevice[deviceID] = { ...state.clips.forDevice[deviceID], loading: true };
		},
		loadClipsSuccess: (state, action) => {
			const { deviceID, clips } = action.payload;
			for (const clip of clips) state.clips.byID[clip.clipID] = stripUrl(clip);
			state.clips.forDevice[deviceID] = { ids: clips.map((c) => c.clipID), loading: false };
		},
		loadClipsError: (state, action) => {
			state.clips.forDevice[action.payload.deviceID] = {
				...state.clips.forDevice[action.payload.deviceID],
				loading: false
			};
		},
		pollClip: () => {},
		// Stop following one clip: the operator dismissed its row, or the poll spent its budget. The
		// clip itself is untouched — it stays in the camera's Clips tab, which is its real home.
		stopWatchingClip: (state, action) => {
			state.clips.watching = state.clips.watching.filter((id) => id !== action.payload.clipID);
		},
		pollClipSuccess: (state, action) => {
			state.clips.byID[action.payload.clip.clipID] = stripUrl(action.payload.clip);
		},
		pollClipError: () => {},
		downloadClip: () => {},
		deleteClip: () => {},
		deleteClipSuccess: (state, action) => {
			const { clipID, deviceID } = action.payload;
			delete state.clips.byID[clipID];
			state.clips.watching = state.clips.watching.filter((id) => id !== clipID);
			const bucket = state.clips.forDevice[deviceID];
			if (bucket) bucket.ids = bucket.ids.filter((id) => id !== clipID);
		},
		deleteClipError: () => {},

		syncSourceSuccess: () => {},
		syncSourceError: (state, action) => {
			const { provider } = action.payload;
			state.sources[provider] = { ...state.sources[provider], syncing: false };
		},

		/*
		 * ── Verify the provider credentials ──────────────────────────────────────
		 *
		 * 🛑 Reached from the INTEGRATIONS SETTINGS page, not from the camera page — it is
		 * the button beside the API key, answering "does this key work and how many cameras
		 * does it see". It lives here because it is the same provider surface as
		 * `syncSource` directly above; the two used to sit in a second camera slice, where
		 * `POST /{provider}/sync` was implemented twice over, once per page.
		 */
		verifyConnection: (state) => {
			state.verify = { loading: true, result: null };
		},
		verifyConnectionSuccess: (state, action) => {
			state.verify = { loading: false, result: action.payload };
		},
		verifyConnectionError: (state, action) => {
			state.verify = { loading: false, result: null };
			state.error = action.payload.error;
		},

		// ── View state ───────────────────────────────────────────────────────────
		/**
		 * 🛑 The window (`from`/`to`) is deliberately NOT reset. An installer comparing two
		 * cameras over one incident picks the period once and then walks the fleet; clearing
		 * it on every selection made that a re-entry of the picker per camera.
		 */
		selectCamera: (state, action) => {
			state.ui.selectedDeviceID = action.payload;
			// 🛑 The seek, and ONLY the seek. It is a moment inside one camera's footage and
			// means nothing on the next one — and while it is set the picture is a replay, so
			// leaving it would open the new camera mid-playback of an event it never had.
			//
			// The WINDOW stays, and now that the mode is derived from it that finally does
			// what the note above always claimed: an installer walking the fleet over one
			// incident picks the period once and every camera opens on it. Before, the tab
			// was reset to `live` underneath the surviving window, so the period was carried
			// and then not shown — the walk cost a click per camera to get back to it.
			state.ui.seekTo = null;
			state.ui.highlight = null;
			// A different camera is a different picture — its own day, not the last one's zoom.
			state.ui.timelineZoom = null;
		},
		/** Mark an event's span on the chart — see `ui.highlight`. Null clears it. */
		setHighlight: (state, action) => {
			state.ui.highlight = action.payload;
		},

		setView: (state, action) => {
			state.ui.view = action.payload;
		},

		setActivityTab: (state, action) => {
			state.ui.activityTab = action.payload;
		},

		setClipMode: (state, action) => {
			state.ui.clipMode = action.payload === true;
			// ⚠️ Closing the bracket throws the period away with it. It is a selection made for one
			// errand, and leaving it standing means the next press of the scissors opens on a
			// period chosen for something the operator has already finished.
			if (!state.ui.clipMode) state.ui.clipRange = null;
		},

		// The rail's narrowing. Null is everything in the scope — see the note in `ui.groupKey`.
		selectGroup: (state, action) => {
			state.ui.groupKey = action.payload ?? null;
		},
		/**
		 * Reorders one space's tiles. The payload is that space's FULL list of deviceIDs
		 * in their new order; anything the list does not mention keeps its natural place
		 * behind them, so a camera arriving from a sync is never dropped by a stale order.
		 */
		reorderCameras: (state, action) => {
			const { spaceKey, order } = action.payload;
			state.ui.order[spaceKey] = order;
		},
		setFilter: (state, action) => {
			state.ui.filter = action.payload;
		},
		setSearch: (state, action) => {
			state.ui.search = action.payload;
		},
		/**
		 * The one way the range moves. The two picker fields, a drag on the timeline and the
		 * "Live" control all land here, so the clamp cannot be skipped by adding a control —
		 * and since the window is now what says whether the picture is live, this is also the
		 * only way that changes.
		 *
		 * Payload carries `anchor` — 'start' or 'end' — naming the instant the operator just
		 * moved, so the clamp knows which one it may push. See clampRange.
		 */
		/**
		 * Back to live, which is the ABSENCE of a chosen window rather than a window that
		 * happens to end now.
		 *
		 * 🛑 It cannot be `setRange({ to: now })`. That writes an explicit `ui.to`, and an
		 * explicit end is exactly what selectIsLive reads as "the operator picked this" —
		 * so the Live button would land on a window the surface then called playback.
		 */
		/**
		 * Magnify the chart around an instant, or clear it with `null`.
		 *
		 * ⚠️ NOT a drag product. The gesture that sets the window must never write this, or the
		 * bars would re-index under the hand that is dragging them.
		 */
		setTimelineZoom: (state, action) => {
			state.ui.timelineZoom = action.payload;
		},

		goLive: (state) => {
			state.ui.from = null;
			state.ui.to = null;
			state.ui.seekTo = null;
			state.ui.highlight = null;
			// Back to live is back to the default picture, magnification included.
			state.ui.timelineZoom = null;
		},

		/**
		 * ⚠️ WHILE THE BRACKET IS OPEN, THE PERIOD CANNOT EXCEED WHAT A CLIP MAY BE — and this is
		 * the ONE place that is enforced, deliberately.
		 *
		 * 🛑 It used to be enforced only as a REFUSAL: the bracket could be swept to any length, the
		 * bar drew it, the readout printed it, and the Create button then greyed out under
		 * "Maximum 20 minutes per clip". So the surface let an operator compose a period at length
		 * and told them it was impossible afterwards — and the amber band, which exists to say what
		 * is going to be cut, was showing something that never could be.
		 *
		 * 🛑 HERE rather than in the gesture, because the gesture is not the only way in. The sweep,
		 * a click on the bar, a skip, an event row and the day picker all arrive through this
		 * reducer, and a cap living in the bracket handler would leave every other one of them able
		 * to put a 24-hour period on an amber band. The bar clamps its own drag as well, so nothing
		 * is ever DRAWN over the cap and then snapped back — but that is a drawing courtesy; this is
		 * the rule.
		 *
		 * ⚠️ The ANCHOR decides which end survives, exactly as it does in `clampRange`: a sweep
		 * leftwards keeps the instant the hand started on.
		 */
		setRange: (state, action) => {
			const { from, to } = clampRange(action.payload);
			state.ui.from = from;
			state.ui.to = to;
		},

		/**
		 * MOVE THE BRACKET. It does NOT touch the window — see `ui.clipRange`.
		 *
		 * 🛑 THAT SEPARATION IS THE WHOLE REDUCER. Every way of moving the bracket used to arrive
		 * through `setRange`: the sweep, a click on the bar, a skip, an event row. So each of them
		 * re-filtered the period, and an operator trimming twenty minutes paid for a fresh mint
		 * and manifest per adjustment while the picture jumped back to the top of the new period.
		 */
		setClipRange: (state, action) => {
			state.ui.clipRange = clipRangeFrom(action.payload);
		},
		setPlanOpen: (state, action) => {
			state.ui.planOpen = action.payload;
		},
		seekTo: (state, action) => {
			state.ui.seekTo = action.payload;
			state.ui.seekNonce = (state.ui.seekNonce ?? 0) + 1;
		}
	},
	extraReducers: (builder) => {
		builder.addCase('auth/logout', () => initialState);
		// 🛑 A group belongs to ONE scope, so a site change makes the narrowing meaningless —
		// and silently so: "360:812" matches nothing in site 999's bucket, and the wall comes
		// up empty with every camera present. Cleared here rather than in the rail because
		// the topbar dropdown changes the scope and never touches this page.
		builder.addCase('app/setSelectedSite', (state) => {
			state.ui.groupKey = null;
		});

		/**
		 * A DIFFERENT ORG IS A DIFFERENT FLEET — so it opens on the wall, not on a camera.
		 *
		 * 🛑 IT USED TO LAND ON ONE. `view` survived the switch and the page auto-selects a camera
		 * whenever the selection is empty (see ../../MainDashboardContainers/Cameras/index.jsx), so
		 * an operator standing on one camera in org A was dropped straight into the detail view of
		 * whichever camera happens to sort first in org B — a camera in a building they have not
		 * chosen to look at, with the old org's period still set over it. Arriving somewhere new
		 * means being shown what is there.
		 *
		 * ⚠️ The SELECTION is cleared too, and the auto-select does put it back — but on the wall,
		 * where a selection only means "this tile is where you were". `view` is what decides which
		 * surface is on screen.
		 *
		 * ⚠️ EVERYTHING THAT NAMED A MOMENT GOES: the window, the seek, the highlight, the zoom, and
		 * clip mode. They were all about footage from another customer's building. This is the one
		 * boundary the "keep the period while walking the fleet" rule (see `selectCamera`) does not
		 * cross.
		 *
		 * ⚠️ `orgHasCameras` is reset because it is an answer ABOUT AN ORG. Left standing, a `true`
		 * from a camera org would tell a camera-less one that its fleet exists and hide the setup
		 * card behind an empty wall.
		 *
		 * ⚠️ ON `switchProfileSuccess` ONLY, never `getUserProfileSuccess` — that one is a
		 * mid-session refresh of the same profile, and resetting on it would throw an operator off
		 * the camera they are watching for no reason they could see.
		 */
		builder.addCase('auth/switchProfileSuccess', (state) => {
			state.orgHasCameras = false;
			state.ui.view = 'all';
			state.ui.selectedDeviceID = null;
			state.ui.groupKey = null;
			state.ui.clipMode = false;
			state.ui.clipRange = null;
			state.ui.from = null;
			state.ui.to = null;
			state.ui.seekTo = null;
			state.ui.highlight = null;
			state.ui.timelineZoom = null;
		});
	}
});

export const camerasActions = camerasSlice.actions;
export default camerasSlice.reducer;
